import React, { useRef } from 'react'
import { InferType } from 'yup'
import { UseFormSetError } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { DiscountCodeForm } from '../DiscountForm'
import { societyDiscountCodeSchema } from '../../../../forms/schemas/society_discount_code'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import {
  getSocietyPremiumInformation,
  societyQueries,
} from '../../../../api/societies'

interface DiscountModalProps {
  show: boolean
  close: () => void
  openPaymentModal: () => void
  setDiscountCode: (code: string) => void
  society: ISocietiesModel
}

export const DiscountModal = ({
  show,
  close,
  setDiscountCode,
  openPaymentModal,
  society,
}: DiscountModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const ref = useRef(null)

  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: async ({
      societyId,
      discountCode,
    }: {
      societyId: string
      discountCode: string
    }) => {
      const response = await getSocietyPremiumInformation(
        societyId,
        discountCode
      )
      const premiumInfo = response.data.data
      if (!premiumInfo?.discountCode) {
        throw new Error('Invalid discount code')
      }
      queryClient.setQueryData(
        societyQueries.getSocietyPremiumInformation(societyId, discountCode)
          .queryKey,
        premiumInfo
      )
      return premiumInfo.discountCode
    },
    onSuccess: (discountCode) => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('invoiceView.flashMessage.discountCodeApplied')
      )
      setDiscountCode(discountCode)
      close()
      openPaymentModal()
    },
  })

  const onSubmit = async (
    data: InferType<typeof societyDiscountCodeSchema>,
    setError: UseFormSetError<InferType<typeof societyDiscountCodeSchema>>
  ): Promise<void> => {
    const { discountCode } = data
    mutate(
      { societyId: society._id, discountCode },
      {
        onError: () => {
          setError('discountCode', {
            type: 'manual',
            message: translate(
              'invoiceView.forms.discountCode.errors.discountCode'
            ),
          })
        },
      }
    )
  }
  return (
    <Modal ref={ref} show={show} size="xl">
      {{
        header: (
          <ModalHeader onClose={close}>
            {translate('invoiceView.modals.discountCode.title')}
          </ModalHeader>
        ),
        body: (
          <ModalBody className="overflow-y-auto">
            <DiscountCodeForm
              onSubmit={onSubmit}
              onClose={close}
              loading={false}
            />
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
